import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Activity, ArrowRight, BookOpen, CalendarRange, Check, CheckSquare, Clock,
  DatabaseBackup, FolderOpen, Hammer, History, KeyRound, Layers, LayoutGrid,
  Library, ListMusic, Lock, Palette, Play, Radio, ScrollText, Search, ServerCog,
  ShieldCheck, Smartphone, Sparkles, Tags, UserSquare, Users, Wand2,
} from 'lucide-react';
import './ShelfPulseHome.css';

const APP_STORE_URL = 'https://apps.apple.com/us/app/shelfpulse/id6754263899';

// Every phone image on the page. Swap a path here when new screenshots land.
const SHOTS = {
  hero: '/images/screenshots/shelfpulse/livesession.jpg',
  stats: '/images/screenshots/shelfpulse/stats.jpg',
  book: '/images/shelfpulse/book-screen.jpg',
  admin: '/images/screenshots/shelfpulse/userstats.jpg',
};

const ANDROID_SHOTS = [
  { src: '/images/shelfpulse/android/live.jpg', alt: 'Live sessions on Android' },
  { src: '/images/shelfpulse/android/users.jpg', alt: 'Users on Android' },
  { src: '/images/shelfpulse/android/user-chart.jpg', alt: 'A user\'s listening chart on Android' },
  { src: '/images/shelfpulse/android/history.jpg', alt: 'Listening history on Android' },
  { src: '/images/shelfpulse/android/stats-growth.jpg', alt: 'Library growth on Android' },
  { src: '/images/shelfpulse/android/book.jpg', alt: 'Book details on Android' },
  { src: '/images/shelfpulse/android/chapters.jpg', alt: 'Chapter editor on Android' },
  { src: '/images/shelfpulse/android/themes.jpg', alt: 'Themes on Android' },
];

// The app's own palettes (ShelfPulse/Theme.swift): primary, secondary, tertiary accent + swatches.
const THEMES = [
  { id: 'sage', name: 'Sophisticated Sage', accents: ['#346751', '#C84B31', '#D4A574'], swatches: ['#346751', '#ECDBBA', '#C84B31', '#2C5F5F', '#D4A574'] },
  { id: 'midnight', name: 'Midnight Slate', accents: ['#715A5A', '#44444E', '#D3DAD9'], swatches: ['#37353E', '#44444E', '#715A5A', '#D3DAD9'] },
  { id: 'forest', name: 'Forest Ember', accents: ['#5A9690', '#2F5755', '#432323'], swatches: ['#432323', '#2F5755', '#5A9690', '#E0D9D9'] },
  { id: 'golden', name: 'Golden Harvest', accents: ['#FCB53B', '#84994F', '#B45253'], swatches: ['#84994F', '#FFE797', '#FCB53B', '#B45253'] },
  { id: 'autumn', name: 'Autumn Breeze', accents: ['#EC5228', '#3F7D58', '#EF9651'], swatches: ['#3F7D58', '#EFEFEF', '#EF9651', '#EC5228'] },
  { id: 'coastal', name: 'Coastal Sunset', accents: ['#EB5B00', '#143D60', '#A0C878'], swatches: ['#DDEB9D', '#A0C878', '#143D60', '#EB5B00'] },
  { id: 'garden', name: 'Garden Bloom', accents: ['#FF9A9A', '#5F8B4C', '#945034'], swatches: ['#5F8B4C', '#FFDDAB', '#FF9A9A', '#945034'] },
  { id: 'twilight', name: 'Twilight Rose', accents: ['#F6ACC8', '#AF8BAF', '#584153'], swatches: ['#F6ACC8', '#AF8BAF', '#584153', '#26191B'] },
  { id: 'meadow', name: 'Meadow Mist', accents: ['#B0C5A4', '#D37676', '#EBC49F'], swatches: ['#B0C5A4', '#D37676', '#EBC49F', '#F1EF99'] },
  { id: 'crimson', name: 'Crimson Dusk', accents: ['#E16A54', '#9F5255', '#F39E60'], swatches: ['#7C444F', '#9F5255', '#E16A54', '#F39E60'] },
  { id: 'electric', name: 'Electric Bloom', accents: ['#D91656', '#EE66A6', '#FFEB55'], swatches: ['#640D5F', '#D91656', '#EE66A6', '#FFEB55'] },
  { id: 'heritage', name: 'Heritage Oak', accents: ['#E07A5F', '#1B4D3E', '#F1E3D3'], swatches: ['#690B22', '#E07A5F', '#F1E3D3', '#1B4D3E'] },
  { id: 'basic', name: 'Primary Basic', accents: ['#0A84FF', '#BF5AF2', '#FF9F0A'], swatches: ['#0A84FF', '#BF5AF2', '#FF9F0A', '#30D158'] },
];

// ---- Colour helpers: lift an accent until it reads on the dark page (as the Android app does) ----

const hexToRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const luminance = ([r, g, b]) => {
  const c = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};

const contrast = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

const mix = (rgb, t) => rgb.map((v) => Math.round(v + (255 - v) * t));

const PAGE_BG = [13, 13, 14];

const liftForDark = (hex) => {
  const rgb = hexToRgb(hex);
  for (let t = 0; t <= 1; t += 0.02) {
    const out = mix(rgb, t);
    if (contrast(out, PAGE_BG) >= 4.5) return `rgb(${out.join(',')})`;
  }
  return '#ffffff';
};

const onColor = (hex) => (contrast(hexToRgb(hex), [0, 0, 0]) >= contrast(hexToRgb(hex), [255, 255, 255]) ? '#0d0d0e' : '#ffffff');

const themeVars = (theme) => ({
  '--a1': liftForDark(theme.accents[0]),
  '--a2': liftForDark(theme.accents[1]),
  '--a3': liftForDark(theme.accents[2]),
  '--a1-raw': theme.accents[0],
  '--a2-raw': theme.accents[1],
  '--a3-raw': theme.accents[2],
  '--on-a1': onColor(theme.accents[0]),
});

const readSavedTheme = () => {
  try {
    return localStorage.getItem('shelfpulse-site-theme') || 'sage';
  } catch {
    return 'sage';
  }
};

// ---- Small pieces ----

function Phone({ src, alt, className = '' }) {
  return (
    <div className={`sph-phone ${className}`}>
      <div className="sph-phone-screen">
        <img src={src} alt={alt} loading="lazy" />
      </div>
    </div>
  );
}

function StoreBadges({ center }) {
  return (
    <div className={`sph-badges ${center ? 'center' : ''}`}>
      <a href={APP_STORE_URL} className="sph-badge" target="_blank" rel="noopener noreferrer">
        <img src="/images/badges/app-store-badge.svg" alt="Download on the App Store" />
      </a>
      <div className="sph-badge sph-badge-soon" aria-label="Google Play, coming soon">
        <img src="/images/badges/google-play-badge.png" alt="" />
        <span>Coming soon</span>
      </div>
    </div>
  );
}

function formatClock(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// A Live card like the app's: the position ticks forward while it's on screen.
function LiveCard() {
  const LENGTH = 16 * 3600 + 10 * 60;
  const [pos, setPos] = useState(5 * 3600 + 42 * 60 + 18);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = setInterval(() => setPos((p) => (p + 1) % LENGTH), 1000);
    return () => clearInterval(id);
  }, [LENGTH]);

  const left = LENGTH - pos;
  return (
    <div className="sph-live-card" aria-hidden="true">
      <div className="sph-live-top">
        <span className="sph-live-user"><Users size={13} /> sarah</span>
        <span className="sph-live-state"><span className="sph-pulse-dot" /> PLAYING</span>
      </div>
      <div className="sph-live-body">
        <img src="/images/covers/project-hail-mary.jpg" alt="" />
        <div>
          <div className="sph-live-title">Project Hail Mary</div>
          <div className="sph-live-author">Andy Weir</div>
          <div className="sph-live-device"><Smartphone size={12} /> Audra · iPhone · iOS 27</div>
        </div>
      </div>
      <div className="sph-live-progress">
        <div className="sph-live-bar"><span style={{ width: `${(pos / LENGTH) * 100}%` }} /></div>
        <div className="sph-live-times">
          <span>{formatClock(pos)}</span>
          <span>{Math.floor(left / 3600)}h {Math.floor((left % 3600) / 60)}m left</span>
        </div>
      </div>
    </div>
  );
}

// Listening chart with the app's calendar / rolling switch.
const CHART = {
  calendar: { label: 'This week', bars: [1.2, 2.6, 0.8, 3.1, 2.2, 0, 0], days: ['M', 'T', 'W', 'T', 'F', 'S', 'S'], total: '9h 54m' },
  rolling: { label: 'Last 7 days', bars: [2.4, 3.6, 1.2, 2.6, 0.8, 3.1, 2.2], days: ['S', 'S', 'M', 'T', 'W', 'T', 'F'], total: '15h 54m' },
};

function StatsDemo() {
  const [mode, setMode] = useState('rolling');
  const data = CHART[mode];
  const max = 4;
  return (
    <div className="sph-demo sph-stats-demo">
      <div className="sph-demo-head">
        <div>
          <div className="sph-demo-label">{data.label}</div>
          <div className="sph-demo-big">{data.total}</div>
        </div>
        <div className="sph-segment" role="group" aria-label="Stats period">
          {['calendar', 'rolling'].map((m) => (
            <button key={m} type="button" className={mode === m ? 'on' : ''} onClick={() => setMode(m)}>
              {m === 'calendar' ? 'Calendar' : 'Rolling'}
            </button>
          ))}
        </div>
      </div>
      <div className="sph-bars">
        {data.bars.map((h, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="sph-bar-col">
            <div className="sph-bar" style={{ height: `${(h / max) * 100}%` }} />
            <span>{data.days[i]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Match results: confidence bars fill when the card scrolls into view.
const MATCHES = [
  { title: 'Project Hail Mary', by: 'Andy Weir · Ray Porter', source: 'Audible', score: 98 },
  { title: 'Project Hail Mary (Dramatized)', by: 'Andy Weir', source: 'Audible', score: 74 },
  { title: 'Hail Mary', by: 'Various', source: 'Google', score: 41 },
];

function MatchDemo() {
  return (
    <div className="sph-demo sph-match-demo">
      <div className="sph-match-search"><Search size={15} /> Project Hail Mary <span>Audible ▾</span></div>
      {MATCHES.map((m, i) => (
        <div key={m.title} className="sph-match-row" style={{ '--d': `${i * 120}ms` }}>
          <div className="sph-match-text">
            <strong>{m.title}</strong>
            <span>{m.by} · {m.source}</span>
          </div>
          <div className="sph-match-score">
            <span>{m.score}%</span>
            <div className="sph-match-meter"><i style={{ '--w': `${m.score}%` }} /></div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Content ----

const TIMELINE = [
  { version: '1.0', title: 'A live dashboard', text: 'Live sessions, listening stats, history, widgets and 13 themes.' },
  { version: '1.0.1', title: 'Sign in your way', text: 'OpenID Connect single sign-on, Tailscale, and the first admin tools.' },
  { version: '1.0.6', title: 'Manage people', text: 'Create, edit and remove users, and choose your own tabs.' },
  { version: '1.0.9', title: 'Collections', text: 'Build and organize collections, and send new users their login.' },
  { version: '2.0', title: 'ShelfPulse grows up', text: 'Edit every book, match metadata, fix chapters, clean up the catalog, run the server. And Android.', now: true },
];

const PILLARS = [
  {
    id: 'watch',
    kicker: 'Watch',
    title: 'See who\'s listening, the moment they press play.',
    body: 'Live shows every listener with the book, their position, how long is left, and the app and device they\'re using. Paused and recently stopped sessions stay visible, so you always know what\'s happening on your server.',
    points: [
      { icon: Radio, text: 'Live cards that update as people listen' },
      { icon: Smartphone, text: 'The app, device and OS behind every session' },
      { icon: History, text: 'Full history with a timeline for every session' },
    ],
    visual: 'live',
  },
  {
    id: 'understand',
    kicker: 'Understand',
    title: 'Every stat, for every listener.',
    body: 'Leaderboards, per-user charts down to the hour, top books and authors, busiest days, and how your library has grown. Count by calendar week and month, or a rolling 7 and 30 days. It\'s your call.',
    points: [
      { icon: Users, text: 'User pages with charts, devices and favourites' },
      { icon: CalendarRange, text: 'Calendar or rolling stats periods' },
      { icon: LayoutGrid, text: 'Server Stats and Recent Activity widgets in three sizes' },
    ],
    visual: 'stats',
  },
  {
    id: 'fix',
    kicker: 'Fix',
    title: 'Fix any book without opening a browser.',
    body: 'Edit titles, authors, narrators, series and covers. Search Audible and other sources, see how close each match is, and choose exactly which fields to take. Then get the chapters right: look them up, shift them, or listen at each mark.',
    points: [
      { icon: Wand2, text: 'Match with a confidence score for every result' },
      { icon: ListMusic, text: 'Chapter editor you can listen through' },
      { icon: Hammer, text: 'Convert to M4B and embed metadata, with live progress' },
    ],
    visual: 'match',
  },
  {
    id: 'run',
    kicker: 'Run',
    title: 'Run the whole server from your phone.',
    body: 'Everything you used to open the web admin for. Add a user and send them their login, reorder libraries and pick folders from the server, check that backups are running, and see when a new Audiobookshelf release is out.',
    points: [
      { icon: Users, text: 'Users, permissions and temporary passwords' },
      { icon: Library, text: 'Create, reorder, scan and edit libraries' },
      { icon: DatabaseBackup, text: 'Backups, API keys, logs and server updates' },
    ],
    visual: 'admin',
  },
];

const BENTO = [
  { icon: CheckSquare, title: 'Edit in bulk', text: 'Select dozens of books to match, re-scan, embed or edit together.', size: 'wide' },
  { icon: UserSquare, title: 'Authors', text: 'Add photos and fill in missing bios in one review.' },
  { icon: Tags, title: 'Genres, tags & narrators', text: 'Merge duplicates, split combined genres, rename, delete.' },
  { icon: Layers, title: 'Series', text: 'Rename and merge series, fix book numbers, add descriptions.' },
  { icon: FolderOpen, title: 'Files & maintenance', text: 'See a book\'s files, re-scan or delete it, clear caches.', size: 'wide' },
  { icon: BookOpen, title: 'Collections', text: 'Create collections and fill them from your library.' },
];

const ADMIN_TILES = [
  { icon: Users, label: 'Users' },
  { icon: Library, label: 'Libraries' },
  { icon: DatabaseBackup, label: 'Backups' },
  { icon: KeyRound, label: 'API keys' },
  { icon: ScrollText, label: 'Server logs' },
  { icon: ServerCog, label: 'Updates' },
  { icon: Hammer, label: 'Maintenance' },
  { icon: Lock, label: 'Temp passwords' },
];

// ---- Page ----

function ShelfPulse() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [themeId, setThemeId] = useState(readSavedTheme);
  const pageRef = useRef(null);

  const theme = THEMES.find((t) => t.id === themeId) || THEMES[0];

  useEffect(() => {
    try {
      localStorage.setItem('shelfpulse-site-theme', themeId);
    } catch {
      // Private mode: the choice just isn't remembered.
    }
  }, [themeId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          observer.unobserve(entry.target);
        }
      }),
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    pageRef.current?.querySelectorAll('.sph-reveal').forEach((el) => observer.observe(el));

    const onScroll = () => setNavScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const jump = (e, id) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  };

  const renderVisual = (visual) => {
    switch (visual) {
      case 'live':
        return (
          <div className="sph-visual-stack">
            <Phone src={SHOTS.hero} alt="ShelfPulse Live sessions" />
            <div className="sph-float sph-float-right"><LiveCard /></div>
          </div>
        );
      case 'stats':
        return (
          <div className="sph-visual-stack">
            <Phone src={SHOTS.stats} alt="ShelfPulse listening statistics" />
            <div className="sph-float sph-float-left"><StatsDemo /></div>
          </div>
        );
      case 'match':
        return (
          <div className="sph-visual-stack">
            <Phone src={SHOTS.book} alt="ShelfPulse book details" />
            <div className="sph-float sph-float-right"><MatchDemo /></div>
          </div>
        );
      default:
        return (
          <div className="sph-admin-grid">
            {ADMIN_TILES.map(({ icon: Icon, label }, i) => (
              <div key={label} className="sph-admin-tile sph-reveal" style={{ '--d': `${i * 50}ms` }}>
                <Icon size={22} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        );
    }
  };

  return (
    <div className="sph" ref={pageRef} style={themeVars(theme)}>
      <Helmet>
        <title>ShelfPulse: Your Audiobookshelf server, in your pocket</title>
        <meta name="description" content="ShelfPulse for iPhone and Android: see who's listening live, dig into stats for every user, fix any book's metadata and chapters, and run users, libraries and backups on your Audiobookshelf server." />
        <link rel="canonical" href="https://audrabooks.app/shelfpulse" />
        <meta property="og:title" content="ShelfPulse: Your Audiobookshelf server, in your pocket" />
        <meta property="og:description" content="Live sessions, stats, book editing, chapters and server admin for Audiobookshelf. On iPhone and Android." />
        <meta property="og:url" content="https://audrabooks.app/shelfpulse" />
        <meta property="og:image" content="https://audrabooks.app/images/shelfpulse/og.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="apple-itunes-app" content="app-id=6754263899" />
      </Helmet>

      {/* Nav */}
      <nav className={`sph-nav ${navScrolled ? 'scrolled' : ''}`}>
        <div className="sph-nav-inner">
          <a href="#top" className="sph-brand" onClick={(e) => jump(e, 'top')}>
            <img src="/images/shelfpulse/icon.png" alt="" />
            <span>ShelfPulse</span>
          </a>
          <button
            type="button"
            className={`sph-burger ${menuOpen ? 'open' : ''}`}
            aria-label="Menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <span /><span /><span />
          </button>
          <div className={`sph-nav-links ${menuOpen ? 'open' : ''}`}>
            <a href="#features" onClick={(e) => jump(e, 'features')}>Features</a>
            <a href="#whats-new" onClick={(e) => jump(e, 'whats-new')}>What's new</a>
            <a href="#themes" onClick={(e) => jump(e, 'themes')}>Themes</a>
            <Link to="/">Audra</Link>
            <a href="#download" className="sph-nav-cta" onClick={(e) => jump(e, 'download')}>Download</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header id="top" className="sph-hero">
        <div className="sph-glow sph-glow-1" />
        <div className="sph-glow sph-glow-2" />
        <div className="sph-wrap sph-hero-grid">
          <div className="sph-hero-copy">
            <div className="sph-pill sph-in"><Sparkles size={14} /> ShelfPulse 2.0 · now for iPhone and Android</div>
            <h1 className="sph-in sph-d1">
              Your Audiobookshelf server,
              <span className="sph-grad"> in your pocket.</span>
            </h1>
            <p className="sph-lead sph-in sph-d2">
              Watch who's listening live. Dig into stats for every listener. Fix any book's
              details and chapters. Run users, libraries and backups. All of it in a fast, native app.
            </p>
            <div className="sph-in sph-d3"><StoreBadges /></div>
            <ul className="sph-hero-facts sph-in sph-d3">
              <li><Check size={15} /> Works with your own server</li>
              <li><Check size={15} /> Single sign-on</li>
              <li><Check size={15} /> No accounts, no analytics</li>
            </ul>
          </div>
          <div className="sph-hero-visual sph-in sph-d2">
            <Phone src={SHOTS.hero} alt="ShelfPulse Live sessions" className="sph-phone-hero" />
            <div className="sph-hero-card"><LiveCard /></div>
            <div className="sph-hero-chip sph-chip-a"><Activity size={16} /> 3 listening now</div>
            <div className="sph-hero-chip sph-chip-b"><Clock size={16} /> 1,284 hours this year</div>
          </div>
        </div>
      </header>

      {/* Capability strip */}
      <div className="sph-strip" aria-label="What ShelfPulse covers">
        <div className="sph-strip-track">
          {[...Array(2)].map((_, k) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={k}>
              {['Live sessions', 'Listening stats', 'User analytics', 'Book editor', 'Metadata match', 'Chapter editor', 'M4B tools', 'Bulk edit', 'Authors', 'Series', 'Genres & tags', 'Users', 'Libraries', 'Backups', 'API keys', 'Server logs', 'Widgets', '13 themes'].map((t) => (
                <span key={`${k}-${t}`}>{t}</span>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Pillars */}
      <main id="features">
        {PILLARS.map((p, i) => (
          <section key={p.id} className={`sph-pillar ${i % 2 ? 'flip' : ''}`}>
            <div className="sph-wrap sph-pillar-grid">
              <div className="sph-pillar-copy sph-reveal">
                <div className="sph-kicker"><span>0{i + 1}</span>{p.kicker}</div>
                <h2>{p.title}</h2>
                <p>{p.body}</p>
                <ul className="sph-points">
                  {p.points.map(({ icon: Icon, text }) => (
                    <li key={text}><span className="sph-point-icon"><Icon size={18} /></span>{text}</li>
                  ))}
                </ul>
              </div>
              <div className="sph-pillar-visual sph-reveal sph-d1">
                {renderVisual(p.visual)}
              </div>
            </div>
          </section>
        ))}

        {/* Catalog bento */}
        <section className="sph-section">
          <div className="sph-wrap">
            <div className="sph-head sph-reveal">
              <div className="sph-kicker center">And the rest of the catalog</div>
              <h2>Clean up years of messy metadata.</h2>
              <p>The jobs that used to take an evening at a desk now take a few taps.</p>
            </div>
            <div className="sph-bento">
              {BENTO.map(({ icon: Icon, title, text, size }, i) => (
                <div key={title} className={`sph-bento-card sph-reveal ${size || ''}`} style={{ '--d': `${i * 60}ms` }}>
                  <div className="sph-bento-icon"><Icon size={22} /></div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Then vs now */}
      <section id="whats-new" className="sph-section sph-timeline-section">
        <div className="sph-wrap">
          <div className="sph-head sph-reveal">
            <div className="sph-kicker center">How far it's come</div>
            <h2>From a dashboard to the whole toolkit.</h2>
            <p>ShelfPulse launched as a way to watch your server. Version 2.0 lets you run it.</p>
          </div>
          <ol className="sph-timeline">
            {TIMELINE.map((t, i) => (
              <li key={t.version} className={`sph-reveal ${t.now ? 'now' : ''}`} style={{ '--d': `${i * 90}ms` }}>
                <div className="sph-tl-dot" />
                <div className="sph-tl-version">v{t.version}</div>
                <h3>{t.title}</h3>
                <p>{t.text}</p>
              </li>
            ))}
          </ol>
          <div className="sph-numbers sph-reveal">
            <div><strong>2</strong><span>platforms, same features</span></div>
            <div><strong>13</strong><span>hand-tuned themes</span></div>
            <div><strong>2</strong><span>home-screen widgets</span></div>
            <div><strong>0</strong><span>browser tabs needed</span></div>
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="sph-section">
        <div className="sph-wrap">
          <div className="sph-head sph-reveal">
            <div className="sph-kicker center">iPhone and Android</div>
            <h2>Native on both. Not a web wrapper.</h2>
            <p>
              Built in SwiftUI for iPhone and Jetpack Compose for Android, with the same features,
              themes and home-screen widgets on each.
            </p>
          </div>
          <div className="sph-android-scroll sph-reveal">
            {ANDROID_SHOTS.map((s) => (
              <img key={s.src} src={s.src} alt={s.alt} loading="lazy" />
            ))}
          </div>
        </div>
      </section>

      {/* Themes */}
      <section id="themes" className="sph-section sph-themes">
        <div className="sph-wrap">
          <div className="sph-head sph-reveal">
            <div className="sph-kicker center"><Palette size={14} /> 13 themes</div>
            <h2>Make it yours. Try one on this page.</h2>
            <p>Every theme works in light and dark mode, and your widgets follow along.</p>
          </div>
          <div className="sph-theme-grid sph-reveal" role="radiogroup" aria-label="Preview a theme">
            {THEMES.map((t) => (
              <button
                key={t.id}
                type="button"
                role="radio"
                aria-checked={t.id === themeId}
                className={`sph-theme ${t.id === themeId ? 'on' : ''}`}
                onClick={() => setThemeId(t.id)}
              >
                <span className="sph-theme-swatches">
                  {t.swatches.map((c) => <i key={c} style={{ background: c }} />)}
                </span>
                <span className="sph-theme-name">{t.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Download */}
      <section id="download" className="sph-section sph-download">
        <div className="sph-wrap">
          <div className="sph-download-card sph-reveal">
            <img className="sph-download-icon" src="/images/shelfpulse/icon.png" alt="" />
            <h2>Put your server in your pocket.</h2>
            <p>ShelfPulse 2.0 is on the App Store now. Android is on its way to Google Play.</p>
            <StoreBadges center />
            <div className="sph-reqs">
              <span><ShieldCheck size={14} /> iOS 18.5 or later</span>
              <span><Play size={14} /> Android 8.0 or later</span>
              <span><ServerCog size={14} /> Any Audiobookshelf server</span>
            </div>
            <p className="sph-fineprint">Server tools appear for admin and root accounts. Everyone gets their own live view, stats and history.</p>
          </div>

          <Link to="/" className="sph-audra sph-reveal">
            <div className="sph-audra-icon"><Play size={20} /></div>
            <div>
              <span className="sph-audra-eyebrow">From the same developer</span>
              <strong>Audra: the Audiobookshelf player for iPhone and Android</strong>
            </div>
            <ArrowRight size={20} className="sph-audra-arrow" />
          </Link>
        </div>
      </section>

      <footer className="sph-footer">
        <div className="sph-wrap sph-footer-inner">
          <div className="sph-brand">
            <img src="/images/shelfpulse/icon.png" alt="" />
            <span>ShelfPulse</span>
          </div>
          <div className="sph-footer-links">
            <Link to="/shelfpulse/privacy">Privacy</Link>
            <Link to="/shelfpulse/terms">Terms</Link>
            <Link to="/">Audra</Link>
          </div>
          <p>&copy; {new Date().getFullYear()} ShelfPulse. Not affiliated with Audiobookshelf.</p>
        </div>
      </footer>
    </div>
  );
}

export default ShelfPulse;
